import { useState, useEffect } from 'react';
import { ForgotPassword, SignIn } from './components';
import { useIntercom } from '@/hooks/useIntercom';
import { signinBanner } from '@/static/images';
import { SignUp } from '../SignUp';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/Button';

export default function Login({ isSignUp = false }: { isSignUp?: boolean }) {
  const [isSignInOpen, setIsSignInOpen] = useState(!isSignUp);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  useIntercom(false); // Add this line to disable Intercom

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const switchView = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsSignInOpen(!isSignInOpen);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 150);
  };

  const switchForgotPasswordView = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsForgotPasswordOpen(!isForgotPasswordOpen);
      setTimeout(() => setIsTransitioning(false), 50);
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const getCurrentForm = () => {
    if (isForgotPasswordOpen) {
      return (
        <ForgotPassword
          navigateToSignIn={() => {
            setIsTransitioning(true);
            setTimeout(() => {
              setIsForgotPasswordOpen(false);
              setIsSignInOpen(true);
              setTimeout(() => setIsTransitioning(false), 50);
            }, 150);
          }}
        />
      );
    } else if (isSignInOpen) {
      return <SignIn navigateToForgotPassword={switchForgotPasswordView} />;
    } else {
      return <SignUp />;
    }
  };

  return (
    <div className="grid w-full h-full sm:h-screen lg:grid-cols-2">
      {/* Left side - Login Form */}
      <div
        className={`flex flex-col gap-6 p-6 px-8 sm:p-6 pt-safe pb-safe pl-safe pr-safe overflow-y-auto bg-white transition-all duration-700 ease-out ${
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div
          className={`flex justify-center gap-2 md:justify-start pt-6 sm:pt-4 sm:pl-2 transition-all duration-500 ease-out delay-100 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
        >
          <Logo type="horizontal" width="120px" height="48px" />
        </div>

        <div className="flex items-center justify-center my-auto py-8 sm:py-6">
          <div
            className={`w-full max-w-sm px-4 sm:px-0 transition-all duration-600 ease-out delay-200 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div
              className={`transition-all duration-300 ease-in-out transform ${
                isTransitioning ? 'opacity-0 scale-95 translate-y-2' : 'opacity-100 scale-100 translate-y-0'
              }`}
            >
              {getCurrentForm()}
            </div>
          </div>
        </div>

        <div
          className={`text-center text-sm pb-8 sm:pb-6 px-4 sm:px-0 transition-all duration-500 ease-out delay-300 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          <div className="space-y-2">
            <div className="text-gray-600">
              {isSignInOpen ? '¿No tienes una cuenta?' : '¿Ya tienes cuenta?'}
            </div>
            <Button
              variant="ghost"
              onClick={switchView}
              onKeyDown={handleKeyDown}
              className="!p-3 px-6 underline underline-offset-4 text-blue-600 hover:text-blue-800 transition-all duration-200 hover:scale-105 active:scale-95 font-medium"
            >
              {isSignInOpen ? 'Registrate' : 'Inicia sesión'}
            </Button>
          </div>
        </div>
      </div>

      {/* Right side - Banner Section */}
      <div
        className={`relative hidden lg:flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-100 p-8 transition-all duration-1000 ease-out delay-100 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div
            className={`absolute top-10 left-10 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl transition-all duration-1000 ease-out delay-500 ${
              isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
            }`}
          ></div>
          <div
            className={`absolute bottom-20 right-10 w-40 h-40 bg-blue-300/20 rounded-full blur-3xl transition-all duration-1000 ease-out delay-700 ${
              isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
            }`}
          ></div>
          <div
            className={`absolute top-1/3 right-20 w-24 h-24 bg-blue-100/40 rounded-full blur-xl transition-all duration-1000 ease-out delay-900 ${
              isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
            }`}
          ></div>
        </div>

        {/* Main content */}
        <div className="relative z-10 flex items-center justify-center w-full h-full">
          <div
            className={`max-w-2xl max-h-2xl flex items-center justify-center transition-all duration-800 ease-out delay-300 ${
              isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <img
              src={signinBanner}
              alt="Ágora - Digitaliza tu negocio"
              className="w-full h-auto max-w-full max-h-full object-contain drop-shadow-lg transition-transform duration-300 hover:scale-105"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
